import React, { useState, useEffect, useMemo } from "react";
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from "chart.js";
import { Pie } from "react-chartjs-2";
import { Row, Col, Nav } from "react-bootstrap";
import { loadingCard } from "../../../helpers/loading";

ChartJS.register(ArcElement, Tooltip, Legend);

interface RawDataItem {
  id?: number;
  resource_name: string;
  resource_type_key: string;
  resource_category: string;
  case_manager: string;
}

interface ResourcePieChartProps {
  data: any;
  showHeader?: boolean;
}

export const ResourceAssignmentPieChart = ({ data, showHeader = false }: ResourcePieChartProps) => {
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const groupings = {
    cm: { name: "By Case Manager" },
    category: { name: "By Category" },
    type: { name: "By Type" },
  };
  const [selectedKey, setSelectedKey] = useState<string>("cm");
  const [cmGroups, setCmGroups] = useState<any[]>();
  const [categoryGroups, setCategoryGroups] = useState<any[]>();
  const [typeGroups, setTypeGroups] = useState<any[]>();

  // Fetch data if not provided
  useEffect(() => {
    if (data) {
      const { resources } = data;
      // setFetchedData(resources);
      const [cmGroups, categoryGroups, typeGroups] = groupData(resources);
      setCmGroups(cmGroups);
      setCategoryGroups(categoryGroups);
      setTypeGroups(typeGroups);
    }
    setIsLoading(false);
  }, [data]);

  function groupData(data: RawDataItem[]) {
    let counts: Record<string, { name: string; count: number }> = {};

    data?.forEach((item) => {
      const key = item.case_manager ?? item.case_manager ?? "NO CASE MANAGER";
      const name = item.case_manager && item.case_manager.trim() !== "" ? item.case_manager.toUpperCase() : "NO CASE MANAGER";
      if (!counts[key]) {
        counts[key] = { name, count: 0 };
      }
      counts[key].count += 1;
    });
    const cmCount = Object.entries(counts)
      .map(([id, { name, count }]) => ({ id, label: name, numberAssignments: count }))
      .sort((a: any, b: any) => a.label.localeCompare(b.label));

    counts = {}; //reset
    data?.forEach((item) => {
      const key = item.resource_category ?? item.resource_category ?? "NO CATEGORY";
      const name = item.resource_category && item.resource_category.trim() !== "" ? item.resource_category.toUpperCase() : "NO CATEGORY";
      if (!counts[key]) {
        counts[key] = { name, count: 0 };
      }
      counts[key].count += 1;
    });
    const catCount = Object.entries(counts)
      .map(([id, { name, count }]) => ({ id, label: name, numberAssignments: count }))
      .sort((a: any, b: any) => a.label.localeCompare(b.label));

    counts = {}; //reset
    data?.forEach((item) => {
      const key = item.resource_type_key ?? item.resource_type_key ?? "NO TYPE";
      const name =
        item.resource_type_key && item.resource_type_key === "consumable"
          ? "CONSUMABLE"
          : item.resource_type_key === "nonconsumable"
            ? "NON-CONSUMABLE"
            : "NO TYPE";
      if (!counts[key]) {
        counts[key] = { name, count: 0 };
      }
      counts[key].count += 1;
    });
    const typeCount = Object.entries(counts)
      .map(([id, { name, count }]) => ({ id, label: name, numberAssignments: count }))
      .sort((a: any, b: any) => a.label.localeCompare(b.label));

    return [cmCount, catCount, typeCount];
  }

  const chartData = useMemo(() => {
    const key = selectedKey === "cm" ? cmGroups : selectedKey === "category" ? categoryGroups : typeGroups;

    return {
      labels: key && key.length > 0 ? key.map((item) => item.label) : ["No data available"],
      datasets: [
        {
          label: "# Assignments",
          data: key && key.length > 0 ? key.map((item) => item.numberAssignments) : [0],
          backgroundColor: ["#ed2f2f", "#DA7E39", "#B2BEB5", "#5A7082", "#5D82A0", "#F4A259", "#B5B2A4", "#A0C1B8", "#E07A5F", "#3D5A80"],
          borderWidth: 1,
        },
      ],
    };
  }, [selectedKey, cmGroups, categoryGroups, typeGroups]);

  const options = {
    plugins: {
      maintainAspectRation: false,
      title: {
        display: true,
        text: `Assignments ${groupings?.[selectedKey as keyof typeof groupings]?.name}`,
        color: "#5a7082",
        font: {
          size: 18,
          weight: 500,
        },
      },
      legend: {
        display: true,
        position: "top" as const,
        align: "center" as const,
        labels: {
          boxWidth: 25,
          font: {
            size: 14,
          },
          padding: 15,
        },
      },
    },
    layout: {
      padding: 0,
    },
  };

  if (isLoading) {
    return loadingCard();
  }

  return (
    <>
      {showHeader && <h5 className="app-header-text">Resource Assignments</h5>}
      <Row className="justify-content-start mb-3">
        <Col xs={5} lg={4}>
          <Nav variant="pills" className="flex-column">
            {Object.keys(groupings).map((key: string) => (
              <Nav.Item className="d-grid" key={key}>
                <Nav.Link
                  eventKey={key}
                  className={selectedKey === key ? "nav-vertical-active" : "nav-vertical"}
                  onClick={() => {
                    setSelectedKey(key);
                  }}
                  style={{ textAlign: "left", fontWeight: 500, cursor: "pointer" }}
                >
                  {groupings[key as keyof typeof groupings].name}
                </Nav.Link>
              </Nav.Item>
            ))}
          </Nav>
        </Col>
        <Col xs={7} lg={8}>
          <div style={{ height: 350, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <Pie data={chartData} options={options} width={350} height={350} style={{ maxHeight: "100%", maxWidth: "100%" }} />
          </div>
        </Col>
      </Row>
    </>
  );
};
